import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { isUpdateCheckPath } from "@xop/dashboard-shared";

import { consumePendingDeepLinkRoute, onDesktopOpenRoute } from "./desktopBridge.js";

/**
 * Navigate the SPA when the desktop app is opened via a share deep-link
 * (`xopstaff://crm/prospects/385`). Mounted once in `AppShell` (inside the
 * Router, so `useNavigate` is available). Two cases, both covered:
 *
 *  - **Cold start** - the OS launched the app BY opening the link: the Rust
 *    shell stashed it; we drain it once on mount via `consumePendingDeepLinkRoute`.
 *  - **Warm open** - the app was already running: the shell emits
 *    `xop://open-route`, picked up by `onDesktopOpenRoute`.
 *
 * No-op in a plain browser (both bridge calls short-circuit when not in Tauri).
 * `navigate` is idempotent, so a (rare) double delivery just re-lands the same
 * route. See docs/desktop-app.md -> "Deep links".
 */
export function useDeepLinkRouting(): void {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    let unlisten: (() => void) | undefined;

    // `DEEP_LINK_UPDATE_PATH` is a SENTINEL, not a route — the release
    // email's "Open the XOP app" CTA. `useDesktopUpdate` handles it (it runs
    // the update check); navigating to it would land on a dead URL, so send
    // the user to the app home instead.
    const go = (path: string): void => {
      navigate(isUpdateCheckPath(path) ? "/" : path);
    };

    void consumePendingDeepLinkRoute().then((path) => {
      if (!cancelled && path) go(path);
    });

    void onDesktopOpenRoute(go).then((fn) => {
      if (cancelled) fn();
      else unlisten = fn;
    });

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [navigate]);
}
